import { IListTimeAssignmentsResponse } from "@/api/timeAssignments/types";
import { toast } from "react-toastify";

interface IUseAssignmentValidationProps {
  week: string[];
  item: IListTimeAssignmentsResponse;
  editedEstimatedTimes: { [taskId: string]: { [date: string]: number } };
}

export const useAssignmentValidation = ({ week, item, editedEstimatedTimes }: IUseAssignmentValidationProps) => {
  const MAX_HOURS_PER_DAY = 10;
  const MAX_HOURS_PER_TASK_PER_DAY = 10;

  /**
   * Valida que una tarea individual no exceda las 10 horas diarias
   */
  const validateSingleTaskHours = (_taskId: string, _date: string, hours: number): boolean => {
    if (hours > MAX_HOURS_PER_TASK_PER_DAY) {
      toast.error(`No se pueden asignar más de ${MAX_HOURS_PER_TASK_PER_DAY} horas diarias por tarea.`, {
        autoClose: 4000,
        hideProgressBar: false,
        closeButton: true,
      });
      return false;
    }
    return true;
  };

  /**
   * Valida que el total de horas diarias no exceda las 10 horas
   */
  const validateDailyTotalHours = (): boolean => {
    let validated = true;



    for (const date of week) {
      let dailyTotal = 0;



      // Para cada asignación, usar horas editadas si existen, sino usar horas existentes
      for (const assignment of item.assignments) {
        const taskId = assignment.task_id;
        const editedHours = editedEstimatedTimes[taskId]?.[date];
        
        if (editedHours !== undefined) {
          // Si hay horas editadas, usar esas (pueden ser 0)
          console.log(`✏️ Task ${taskId}: using edited ${editedHours}h`);
          dailyTotal += editedHours;
        } else {
          // SOLO usar horas existentes si NO HAY NINGUNA edición para este día
          // Si hay cualquier edición en este día, asumir que las tareas no editadas son 0
          const hasAnyEditForThisDate = Object.values(editedEstimatedTimes).some(
            taskDates => taskDates && taskDates[date] !== undefined
          );
          
          if (hasAnyEditForThisDate) {
            // Si hay ediciones en este día, las tareas no editadas se consideran 0
            console.log(`🔄 Task ${taskId}: assuming 0h (other tasks edited on this date)`);
            dailyTotal += 0;
          } else {
            // Si no hay ediciones en este día, usar horas existentes
            const existingSchedule = assignment.schedules.find(s => 
              s.scheduleDate.substring(0, 10) === date
            );
            if (existingSchedule) {
              const existingHours = Number(existingSchedule.estimated_time) || 0;
              console.log(`📅 Task ${taskId}: using existing ${existingHours}h`);
              dailyTotal += existingHours;
            } else {
              console.log(`❌ Task ${taskId}: no schedule found for ${date}`);
            }
          }
        }
      }

      console.log(`📈 Daily total for ${date}: ${dailyTotal}h`);

      if (dailyTotal > MAX_HOURS_PER_DAY) {
        const dateFormatted = new Date(date).toLocaleDateString('es-ES', {
          weekday: 'long',
          day: 'numeric',
          month: 'short'
        });
        
        console.log(`❌ VALIDATION FAILED: ${dailyTotal}h > ${MAX_HOURS_PER_DAY}h`);
        
        toast.error(`El total de horas asignadas para ${dateFormatted} (${dailyTotal.toFixed(1)}h) excede el límite de ${MAX_HOURS_PER_DAY} horas diarias.`, {
          autoClose: 5000,
          hideProgressBar: false,
          closeButton: true,
        });
        validated = false;
        break;
      }
    }

    return validated;
  };

  /**
   * Validación principal que incluye ambas reglas
   */
  const validateScheduleChange = (): boolean => {
    // Primero validar cada tarea individualmente
    for (const [taskId, dates] of Object.entries(editedEstimatedTimes)) {
      for (const [date, hours] of Object.entries(dates)) {
        if (!validateSingleTaskHours(taskId, date, hours)) {
          return false;
        }
      }
    }

    // Luego validar el total diario
    return validateDailyTotalHours();
  };

  /**
   * Valida una entrada específica antes de aplicarla
   */
  const validateHourInput = (taskId: string, date: string, newHours: number): boolean => {

    
    // Validar que la tarea individual no exceda 10 horas
    if (!validateSingleTaskHours(taskId, date, newHours)) {
      return false;
    }

    // Calcular el total diario si se aplicara este cambio
    let dailyTotal = 0;
    
    // Crear una copia temporal de editedEstimatedTimes con el nuevo valor
    const tempEditedTimes = {
      ...editedEstimatedTimes,
      [taskId]: {
        ...editedEstimatedTimes[taskId],
        [date]: newHours
      }
    };
    

    
    for (const assignment of item.assignments) {
      const assignmentTaskId = assignment.task_id;
      const editedHours = tempEditedTimes[assignmentTaskId]?.[date];
      
      if (editedHours !== undefined) {
        // Si hay horas editadas (incluyendo el nuevo valor), usar esas

        dailyTotal += editedHours;
      } else {
        // SOLO usar horas existentes si NO HAY NINGUNA edición para este día
        // Si hay cualquier edición en este día, asumir que las tareas no editadas son 0
        const hasAnyEditForThisDate = Object.values(tempEditedTimes).some(
          taskDates => taskDates && taskDates[date] !== undefined
        );
        
        if (hasAnyEditForThisDate) {
          // Si hay ediciones en este día, las tareas no editadas se consideran 0
          console.log(`🔄 Task ${assignmentTaskId}: assuming 0h (other tasks edited on this date)`);
          dailyTotal += 0;
        } else {
          // Si no hay ediciones en este día, usar horas existentes
          const existingSchedule = assignment.schedules.find(s => 
            s.scheduleDate.substring(0, 10) === date
          );
          if (existingSchedule) {
            const existingHours = Number(existingSchedule.estimated_time) || 0;
            console.log(`📅 Task ${assignmentTaskId}: using existing ${existingHours}h`);
            dailyTotal += existingHours;
          } else {
            console.log(`❌ Task ${assignmentTaskId}: no schedule found for ${date}`);
          }
        }
      }
    }
    
    console.log(`📈 TOTAL for ${date}: ${dailyTotal}h (limit: ${MAX_HOURS_PER_DAY}h)`);

    if (dailyTotal > MAX_HOURS_PER_DAY) {
      const dateFormatted = new Date(date).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      });
      
      toast.error(`El total de horas para ${dateFormatted} sería ${dailyTotal.toFixed(1)}h, excediendo el límite de ${MAX_HOURS_PER_DAY} horas diarias.`, {
        autoClose: 5000,
        hideProgressBar: false,
        closeButton: true,
      });
      return false;
    }

    return true;
  };

  const checkAssignment = (): void => {
    toast.error("El colaborador excede el límite de tiempo por día, por favor revise la asignación de la tarea.", {
      autoClose: 3000,
      hideProgressBar: true,
      closeButton: false,
    });
  };

  return { 
    validateScheduleChange, 
    validateHourInput,
    checkAssignment,
    MAX_HOURS_PER_DAY,
    MAX_HOURS_PER_TASK_PER_DAY
  };
};